import { fastFacts, heroFeature, heroSideCards } from '@/mocks/home';
import { useCountdownTo } from '@/hooks/useCountdownTo';

const toneIconClasses: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-700',
  secondary: 'bg-secondary-100 text-secondary-800',
};

const toneValueClasses: Record<string, string> = {
  primary: 'text-foreground-950',
  accent: 'text-accent-700',
  secondary: 'text-foreground-900',
};

export default function HeroBoard() {
  const countdown = useCountdownTo(18, 0);

  return (
    <section id="top" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 items-center gap-1.5 rounded-full bg-accent-500 px-3 font-label text-xs font-medium text-background-50 dark:text-foreground-950">
              <i className="ri-timer-flash-line" />
              六点整发布
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">今日六点场 · 情报台</h2>
          </div>
          <p className="font-label text-xs text-foreground-600">
            到点自动汇总新瓜与反转折点，一条时间线看完整场
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <article className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:col-span-6">
            <div className="relative h-56 w-full overflow-hidden md:h-72">
              <img
                src={heroFeature.image}
                alt="黑料六点今日焦点爆料氛围图"
                title="黑料六点 今日焦点爆料"
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/75 via-foreground-950/25 to-transparent" />
              <div className="absolute left-4 top-4 flex items-center gap-2">
                <span className="flex h-7 items-center rounded-full bg-primary-500 px-3 font-label text-xs font-medium text-background-50 dark:text-foreground-950">
                  {heroFeature.tag}
                </span>
                <span className="flex h-7 items-center rounded-full bg-background-50/90 px-3 font-label text-xs text-foreground-800">
                  {heroFeature.topic}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="font-label text-xs text-background-200">
                  {heroFeature.time} 发布 · 已有 {heroFeature.followed} 家账号跟进
                </p>
                <h3 className="mt-2 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  {heroFeature.title}
                </h3>
              </div>
            </div>
            <div className="flex flex-col gap-3 p-4 md:p-5">
              <p className="text-sm leading-relaxed text-foreground-700">{heroFeature.summary}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-7 items-center gap-1.5 rounded-md bg-accent-100 px-2.5 font-label text-xs font-medium text-accent-700">
                  <i className="ri-fire-line" />
                  {heroFeature.heatLabel} {heroFeature.heat.toLocaleString()}
                </span>
                <span className="flex h-7 items-center gap-1.5 rounded-md bg-secondary-100 px-2.5 font-label text-xs text-secondary-900">
                  <i className="ri-loader-4-line" />
                  编辑已发起求证
                </span>
                <a
                  href="#feed"
                  className="ml-auto flex h-7 items-center gap-1 whitespace-nowrap font-label text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  看时间线
                  <i className="ri-arrow-right-s-line" />
                </a>
              </div>
            </div>
          </article>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-6">
            <article className="flex flex-col justify-between rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <span className="font-label text-xs font-medium text-primary-700">{heroSideCards[0].label}</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={heroSideCards[0].icon} />
                </span>
              </div>
              <div className="mt-4">
                <p className="font-heading text-3xl font-bold tabular-nums tracking-tight text-foreground-950">
                  {countdown.label}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{heroSideCards[0].hint}</p>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className="relative flex h-2 w-2 items-center justify-center">
                  <span className="absolute inline-flex h-2 w-2 animate-pulse-ring rounded-full bg-primary-400" />
                  <span className="inline-flex h-2 w-2 rounded-full bg-primary-500" />
                </span>
                <span className="font-label text-xs text-primary-700">
                  {countdown.isToday ? '今日场次倒计时中' : '已进入次日场次倒计时'}
                </span>
              </div>
            </article>

            <article className="flex flex-col justify-between rounded-lg border border-accent-200 bg-accent-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <span className="font-label text-xs font-medium text-accent-700">{heroSideCards[1].label}</span>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneIconClasses.accent}`}>
                  <i className={heroSideCards[1].icon} />
                </span>
              </div>
              <div className="mt-4">
                <p className={`font-heading text-lg font-bold leading-snug ${toneValueClasses.accent}`}>
                  {heroSideCards[1].value}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{heroSideCards[1].hint}</p>
              </div>
              <a
                href="#ranking"
                className="mt-4 flex h-7 items-center gap-1 whitespace-nowrap font-label text-xs font-medium text-accent-700 transition-colors hover:text-accent-600"
              >
                查看完整热度榜
                <i className="ri-arrow-right-s-line" />
              </a>
            </article>

            <article className="flex flex-col justify-between rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <span className="font-label text-xs font-medium text-foreground-600">{heroSideCards[2].label}</span>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneIconClasses.secondary}`}>
                  <i className={heroSideCards[2].icon} />
                </span>
              </div>
              <div className="mt-4">
                <p className="font-heading text-3xl font-bold tracking-tight text-foreground-950">
                  {heroSideCards[2].value}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{heroSideCards[2].hint}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {fastFacts.map((fact) => (
                  <span
                    key={fact.label}
                    className="flex h-7 items-center gap-1.5 rounded-md bg-background-200 px-2.5 font-label text-xs text-foreground-700"
                  >
                    {fact.label}
                    <strong className="font-bold text-foreground-950">{fact.value}</strong>
                  </span>
                ))}
              </div>
            </article>

            <article className="flex flex-col justify-between rounded-lg border border-secondary-200 bg-secondary-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <span className="font-label text-xs font-medium text-secondary-800">{heroSideCards[3].label}</span>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneIconClasses.secondary}`}>
                  <i className={heroSideCards[3].icon} />
                </span>
              </div>
              <div className="mt-4">
                <p className="font-heading text-lg font-bold leading-snug text-foreground-950">
                  {heroSideCards[3].value}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{heroSideCards[3].hint}</p>
              </div>
              <a
                href="#reversal"
                className="mt-4 flex h-7 items-center gap-1 whitespace-nowrap font-label text-xs font-medium text-secondary-800 transition-colors hover:text-secondary-700"
              >
                进入反转追踪
                <i className="ri-arrow-right-s-line" />
              </a>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}